import * as React from 'react';
import { useSelector } from 'react-redux';
import { juxt } from 'ramda';

import { SearchTextInput } from '../../../components/SearchText';
import { getSearchText } from '../../../selectors/filters';
import { useSetSearchTextHandler } from '../../../hooks/useSetSearchTextHandler';
import { useGetListingHandler } from '../../../hooks/useGetListingHandler';
import { SearchHistoryStorage } from './utils/searchHistoryStorage';
import { useHistoryListWrapper } from './utils/useHistoryListWrapper';
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch';

export interface ISearchTextInputContainerProps {
  inputWidth?: number | string;
  onHistoryListOpenChanged?(isOpen: boolean): void;
}

export function SearchTextInputContainer({ inputWidth, onHistoryListOpenChanged }: ISearchTextInputContainerProps) {
  let timeoutId: number;

  const dispatch = useAppDispatch();
  const searchText = useSelector(getSearchText);
  const setSearchText = useSetSearchTextHandler();
  const getListing = useGetListingHandler();

  const searchHistoryStorage = React.useMemo(() => new SearchHistoryStorage(), []);

  const handleInputChange = React.useCallback(
    (newValue: string) => {
      window.clearTimeout(timeoutId);

      dispatch(setSearchText(newValue || undefined));

      // oxlint-disable-next-line react/exhaustive-deps
      timeoutId = window.setTimeout(() => {
        dispatch(getListing(1));
      }, 1000);

      return;
    },
    [dispatch, getListing, setSearchText],
  );

  const onHistoryItemSelect = React.useCallback(
    (selectedItem: string) => {
      dispatch(setSearchText(selectedItem));
      dispatch(getListing(1));
    },
    [dispatch, getListing, setSearchText],
  );

  const handleInputClear = React.useCallback(() => {
    handleInputChange('');
  }, [handleInputChange]);

  const { handleSearchInputBlur, handleSearchInputFocus, handleSearchInputChange, renderSearchHistoryWrapper } =
    useHistoryListWrapper({ searchHistoryStorage, onHistoryItemSelect, onHistoryListOpenChanged });

  return renderSearchHistoryWrapper(
    <SearchTextInput
      width={inputWidth}
      value={searchText}
      clearInput={handleInputClear}
      onChange={juxt([handleInputChange, handleSearchInputChange])}
      onFocus={handleSearchInputFocus}
      onBlur={handleSearchInputBlur}
    />,
  );
}
